import { useState, useEffect, useCallback, Component } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Boxes, AlertTriangle, PackageSearch, Truck, LineChart, Building2,
  ClipboardCheck, Plus, Pencil, Archive, RotateCcw, Play, Upload, Download, CheckCircle2,
  MapPin, Bell,
} from 'lucide-react';

const API = '/api';

// Phase 1 of the inventory module. Deliberately NOT the whole spec: purchase orders,
// transfers, the Australia views, reports/cashflow, R&D, the Vendor Order Planner and the AU
// Replenishment view are later phases. What is here is the data layer plus the screens the
// build command names as gating everything downstream — vendors, forecasts, products, and
// counts — because nothing else can be computed until those are populated.

// Same class fix as Customer Replies: one bad row must never blank the page.
class Boundary extends Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error(`[Inventory] ${this.props.name} failed`, err, info); }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="border border-red-400/60 rounded-sm p-3 text-[12px] font-sans text-red-300">
        <AlertTriangle size={12} className="inline mr-1.5" />
        “{this.props.name}” could not be displayed: {String(this.state.err?.message || this.state.err)}
      </div>
    );
  }
}

const N = (v, d = '—') => (v === null || v === undefined || v === '' ? d : v);
const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const num = (v, dp = 0) => (v === null || v === undefined ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: dp }));

const STATUS_STYLE = {
  out: 'text-red-300 border-red-400/60',
  critical: 'text-red-300 border-red-400/60',
  order_now: 'text-orange-300 border-orange-400/60',
  order_soon: 'text-amber-300 border-amber-400/60',
  low: 'text-yellow-300 border-yellow-400/50',
  ok: 'text-hg-green border-hg-green/50',
  no_forecast: 'text-hg-text-dim border-hg-border',
};
const STATUS_LABEL = {
  out: 'Out', critical: 'Critical', order_now: 'Order now', order_soon: 'Order soon',
  low: 'Low', ok: 'OK', no_forecast: 'No forecast',
};

function Chip({ status }) {
  return (
    <span className={`text-[9px] font-sans uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_STYLE[status] || STATUS_STYLE.no_forecast}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function Stat({ label, value, tone = 'text-hg-text', hint }) {
  return (
    <div className="bg-hg-surface border border-hg-border rounded-sm p-3">
      <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">{label}</div>
      <div className={`font-display text-2xl mt-1 ${tone}`}>{value}</div>
      {hint && <div className="text-[10px] font-sans text-hg-text-dim mt-0.5">{hint}</div>}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] font-sans text-hg-text-dim mt-0.5">{hint}</div>}
    </label>
  );
}

const inputCls = 'w-full bg-hg-bg border border-hg-border rounded-sm px-2.5 py-1.5 text-xs font-sans text-hg-text';
const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans border rounded-sm disabled:opacity-40';
const btnGreen = `${btn} border-hg-green text-hg-green hover:bg-hg-green/10`;
const btnPlain = `${btn} border-hg-border text-hg-text-dim hover:text-hg-text`;

// ============================================================ OVERVIEW
function Overview({ summary, alerts, onRun, running, reload }) {
  const [ack, setAck] = useState('');
  const doAck = async (id) => {
    setAck(id);
    try { await axios.post(`${API}/inventory/alerts/${id}/acknowledge`); await reload(); }
    catch (e) { window.alert(e.response?.data?.detail || 'Could not acknowledge.'); }
    setAck('');
  };
  const s = summary || {};
  const gaps = [
    ['products_without_forecast', 'no annual forecast', 'excluded from every runout calculation'],
    ['products_without_vendor', 'no vendor', 'no lead time, no MOQ, no auto-PO'],
    ['products_without_cost', 'no unit cost', 'order values and deposits read $0'],
  ].filter(([k]) => (s[k] || 0) > 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Critical alerts" value={num(s.critical_alerts)} tone={s.critical_alerts ? 'text-red-300' : 'text-hg-text'} />
        <Stat label="Warnings" value={num(s.warning_alerts)} tone={s.warning_alerts ? 'text-amber-300' : 'text-hg-text'} />
        <Stat label="POs awaiting approval" value={num(s.pos_pending_approval)} />
        <Stat label="Active products" value={num(s.active_products)} hint={`${num(s.total_products)} total · ${num(s.vendors)} vendors`} />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onRun} disabled={running} className={btnGreen} data-testid="inv-run-check">
          {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run inventory check
        </button>
        <span className="text-[10px] font-sans text-hg-text-dim">
          Writes alerts and drafts POs for approval. Sends nothing.
        </span>
      </div>

      {gaps.length > 0 && (
        <div className="border border-amber-400/50 rounded-sm p-3 space-y-1" data-testid="inv-gaps">
          <div className="text-[11px] font-sans text-amber-300 inline-flex items-center gap-1.5">
            <AlertTriangle size={12} /> Planning gaps — these block the forecast math
          </div>
          {gaps.map(([k, what, why]) => (
            <div key={k} className="text-[11px] font-sans text-hg-text-dim">
              <span className="text-hg-text">{num(s[k])}</span> active products have {what} — {why}.
            </div>
          ))}
          <div className="text-[10px] font-sans text-hg-text-dim pt-1">
            Fastest path: Products → Export CSV, fill costs and vendors in Excel, then Import CSV.
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">Physical counts</div>
        {(s.count_status || []).map((c) => (
          <div key={c.facility_id} className="flex items-center gap-2 text-[11px] font-sans bg-hg-surface border border-hg-border rounded-sm px-3 py-2">
            <MapPin size={11} className="text-hg-text-dim flex-shrink-0" />
            <span className="text-hg-text">{c.facility_name}</span>
            <span className="text-hg-text-dim">
              last counted {c.last_counted_date || 'never'}
              {c.days_since_count !== null && c.days_since_count !== undefined ? ` · ${c.days_since_count} days ago` : ''}
            </span>
            {c.count_due && (
              <span className="ml-auto text-amber-300 border border-amber-400/50 rounded-sm px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
                count due
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
          Active alerts ({(alerts || []).length})
        </div>
        {(alerts || []).length === 0 && (
          <div className="text-[12px] font-sans text-hg-text-dim italic">
            No active alerts. {s.products_without_forecast > 0
              ? `Note: ${num(s.products_without_forecast)} products have no forecast, so they cannot raise one yet.`
              : ''}
          </div>
        )}
        {(alerts || []).map((a) => (
          <Boundary key={a.id} name={`alert ${a.id}`}>
            <div className="flex items-start gap-2 bg-hg-surface border border-hg-border rounded-sm px-3 py-2">
              <Bell size={11} className={`flex-shrink-0 mt-0.5 ${a.severity === 'critical' ? 'text-red-300' : a.severity === 'warning' ? 'text-amber-300' : 'text-hg-text-dim'}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-sans text-hg-text">{a.message}</div>
                <div className="text-[10px] font-sans text-hg-text-dim mt-0.5">
                  {a.product_code} · {a.facility_name}
                  {a.resolves_via === 'transfer' && (
                    <span className="ml-1.5 text-purple-300 border border-purple-400/40 rounded-sm px-1 py-0.5 text-[9px] uppercase tracking-wider">
                      resolves via transfer
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => doAck(a.id)} disabled={ack === a.id} className={`${btnPlain} flex-shrink-0`}>
                {ack === a.id ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Acknowledge
              </button>
            </div>
          </Boundary>
        ))}
      </div>
    </div>
  );
}

// ============================================================ VENDORS
const emptyVendor = { name: '', contact_name: '', email: '', phone: '', country: '', payment_terms: '', deposit_percent: '', lead_time_days: '', production_days: '', transit_days: '', min_order_value: '', min_order_units: '', order_schedule_notes: '', shipping_cost_flat: '', shipping_cost_percent: '', import_tariff_percent: '', other_import_costs_percent: '', notes: '', status: 'active' };

function Vendors({ reload }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/inventory/vendors`);
    setRows(r.data.vendors || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...edit };
      Object.keys(body).forEach((k) => { if (body[k] === '') body[k] = null; });
      body.name = (edit.name || '').trim();
      if (!body.name) throw new Error('Vendor name is required.');
      if (edit.id) await axios.put(`${API}/inventory/vendors/${edit.id}`, body);
      else await axios.post(`${API}/inventory/vendors`, body);
      setEdit(null); await load(); await reload();
    } catch (e) { window.alert(e.response?.data?.detail || e.message || 'Save failed.'); }
    setSaving(false);
  };

  if (!rows) return <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-sans text-hg-text-dim">
          {rows.length} vendors. Lead time, MOQ and cost adders come from here — every order date
          downstream depends on them.
        </div>
        <button onClick={() => setEdit({ ...emptyVendor })} className={btnGreen} data-testid="inv-vendor-new">
          <Plus size={12} /> New vendor
        </button>
      </div>
      {rows.length === 0 && (
        <div className="text-[12px] font-sans text-hg-text-dim italic">
          No vendors yet. Base44 had none — every vendor has to be entered here, and products
          cannot compute a lead time or an order-by date until they are.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {rows.map((v) => (
          <Boundary key={v.id} name={`vendor ${v.id}`}>
            <div className="bg-hg-surface border border-hg-border rounded-sm p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-sans text-hg-text">{v.name}</div>
                  <div className="text-[10px] font-sans text-hg-text-dim">
                    {N(v.contact_name, '')} {v.email ? `· ${v.email}` : ''} {v.country ? `· ${v.country}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {v.status !== 'active' && <span className="text-[9px] uppercase tracking-wider text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5">inactive</span>}
                  <button onClick={() => setEdit({ ...emptyVendor, ...v })} className={btnPlain}><Pencil size={11} /> Edit</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-[11px] font-sans">
                <div className="text-hg-text-dim">Effective lead <span className="text-hg-text">{v.effective_lead_days} days</span></div>
                <div className="text-hg-text-dim">Terms <span className="text-hg-text">{N(v.payment_terms)}</span></div>
                <div className="text-hg-text-dim">Deposit <span className="text-hg-text">{v.deposit_percent ? `${v.deposit_percent}%` : '—'}</span></div>
                <div className="text-hg-text-dim">Min order <span className="text-hg-text">{v.min_order_value ? money(v.min_order_value) : '—'}{v.min_order_units ? ` / ${v.min_order_units}u` : ''}</span></div>
                <div className="text-hg-text-dim">Landed adder <span className="text-hg-text">×{v.landed_cost_multiplier}</span></div>
                <div className="text-hg-text-dim">Products <span className="text-hg-text">{v.product_count}</span></div>
              </div>
              {v.order_schedule_notes && (
                <div className="text-[10px] font-sans text-amber-300 mt-1.5">{v.order_schedule_notes}</div>
              )}
            </div>
          </Boundary>
        ))}
      </div>

      {edit && (
        <div className="border border-hg-green/40 rounded-sm p-3 space-y-2 bg-hg-bg/40" data-testid="inv-vendor-form">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
            {edit.id ? 'Edit vendor' : 'New vendor'}
          </div>
          <div className="grid md:grid-cols-3 gap-2">
            <Field label="Name *"><input className={inputCls} value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Contact"><input className={inputCls} value={edit.contact_name || ''} onChange={(e) => setEdit({ ...edit, contact_name: e.target.value })} /></Field>
            <Field label="Email"><input className={inputCls} value={edit.email || ''} onChange={(e) => setEdit({ ...edit, email: e.target.value })} /></Field>
            <Field label="Phone"><input className={inputCls} value={edit.phone || ''} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></Field>
            <Field label="Country"><input className={inputCls} value={edit.country || ''} onChange={(e) => setEdit({ ...edit, country: e.target.value })} /></Field>
            <Field label="Payment terms"><input className={inputCls} value={edit.payment_terms || ''} onChange={(e) => setEdit({ ...edit, payment_terms: e.target.value })} /></Field>
            <Field label="Production days" hint="production + transit wins over lead time"><input type="number" className={inputCls} value={edit.production_days ?? ''} onChange={(e) => setEdit({ ...edit, production_days: e.target.value })} /></Field>
            <Field label="Transit days"><input type="number" className={inputCls} value={edit.transit_days ?? ''} onChange={(e) => setEdit({ ...edit, transit_days: e.target.value })} /></Field>
            <Field label="Lead time days" hint="fallback if the two above are blank; defaults to 30"><input type="number" className={inputCls} value={edit.lead_time_days ?? ''} onChange={(e) => setEdit({ ...edit, lead_time_days: e.target.value })} /></Field>
            <Field label="Deposit %"><input type="number" className={inputCls} value={edit.deposit_percent ?? ''} onChange={(e) => setEdit({ ...edit, deposit_percent: e.target.value })} /></Field>
            <Field label="Min order value $"><input type="number" className={inputCls} value={edit.min_order_value ?? ''} onChange={(e) => setEdit({ ...edit, min_order_value: e.target.value })} /></Field>
            <Field label="Min order units"><input type="number" className={inputCls} value={edit.min_order_units ?? ''} onChange={(e) => setEdit({ ...edit, min_order_units: e.target.value })} /></Field>
            <Field label="Shipping flat $"><input type="number" className={inputCls} value={edit.shipping_cost_flat ?? ''} onChange={(e) => setEdit({ ...edit, shipping_cost_flat: e.target.value })} /></Field>
            <Field label="Shipping %"><input type="number" className={inputCls} value={edit.shipping_cost_percent ?? ''} onChange={(e) => setEdit({ ...edit, shipping_cost_percent: e.target.value })} /></Field>
            <Field label="Import tariff %"><input type="number" className={inputCls} value={edit.import_tariff_percent ?? ''} onChange={(e) => setEdit({ ...edit, import_tariff_percent: e.target.value })} /></Field>
            <Field label="Other import %"><input type="number" className={inputCls} value={edit.other_import_costs_percent ?? ''} onChange={(e) => setEdit({ ...edit, other_import_costs_percent: e.target.value })} /></Field>
            <Field label="Status">
              <select className={inputCls} value={edit.status || 'active'} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                <option value="active">active</option><option value="inactive">inactive</option>
              </select>
            </Field>
          </div>
          <Field label="Order schedule notes" hint='e.g. "factory closes for Chinese New Year", "orders monthly on the 1st"'>
            <input className={inputCls} value={edit.order_schedule_notes || ''} onChange={(e) => setEdit({ ...edit, order_schedule_notes: e.target.value })} />
          </Field>
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className={btnGreen} data-testid="inv-vendor-save">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save
            </button>
            <button onClick={() => setEdit(null)} className={btnPlain}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================ FACILITIES
function Facilities({ reload }) {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    const r = await axios.get(`${API}/inventory/facilities`);
    setRows(r.data.facilities || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...edit, is_australia: !!edit.is_australia };
      if (!(body.name || '').trim()) throw new Error('Name is required.');
      if (edit.id) await axios.put(`${API}/inventory/facilities/${edit.id}`, body);
      else await axios.post(`${API}/inventory/facilities`, body);
      setEdit(null); await load(); await reload();
    } catch (e) { window.alert(e.response?.data?.detail || e.message || 'Save failed.'); }
  };

  if (!rows) return <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-sans text-hg-text-dim">
          The <span className="text-hg-text">Australia</span> flag drives region selection — AU
          demand and the AU seasonal curve. It is data, not a hardcoded id.
        </div>
        <button onClick={() => setEdit({ name: '', type: 'satellite', is_australia: false })} className={btnGreen}>
          <Plus size={12} /> New facility
        </button>
      </div>
      {rows.map((f) => (
        <Boundary key={f.id} name={`facility ${f.id}`}>
          <div className="bg-hg-surface border border-hg-border rounded-sm px-3 py-2 flex items-center gap-2">
            <Building2 size={12} className="text-hg-text-dim flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-sans text-hg-text">
                {f.name}
                <span className="ml-2 text-[9px] uppercase tracking-wider text-hg-text-dim border border-hg-border rounded-sm px-1.5 py-0.5">{f.type}</span>
                {f.is_australia && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-purple-300 border border-purple-400/40 rounded-sm px-1.5 py-0.5">Australia</span>}
              </div>
              <div className="text-[10px] font-sans text-hg-text-dim">
                {[f.city, f.state].filter(Boolean).join(', ') || '—'}
                {' · '}last counted {f.count_status?.last_counted_date || 'never'}
                {f.count_status?.count_due && <span className="text-amber-300"> · count due</span>}
              </div>
            </div>
            <button onClick={() => setEdit({ ...f })} className={btnPlain}><Pencil size={11} /> Edit</button>
          </div>
        </Boundary>
      ))}
      {edit && (
        <div className="border border-hg-green/40 rounded-sm p-3 space-y-2 bg-hg-bg/40">
          <div className="grid md:grid-cols-3 gap-2">
            <Field label="Name *"><input className={inputCls} value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
            <Field label="Type">
              <select className={inputCls} value={edit.type || 'satellite'} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>
                <option value="main">main</option><option value="satellite">satellite</option>
              </select>
            </Field>
            <Field label="Australia facility" hint="drives AU demand + AU seasonality">
              <label className="inline-flex items-center gap-2 text-xs font-sans text-hg-text mt-1.5">
                <input type="checkbox" checked={!!edit.is_australia} onChange={(e) => setEdit({ ...edit, is_australia: e.target.checked })} />
                is_australia
              </label>
            </Field>
            <Field label="Address"><input className={inputCls} value={edit.address || ''} onChange={(e) => setEdit({ ...edit, address: e.target.value })} /></Field>
            <Field label="City"><input className={inputCls} value={edit.city || ''} onChange={(e) => setEdit({ ...edit, city: e.target.value })} /></Field>
            <Field label="State"><input className={inputCls} value={edit.state || ''} onChange={(e) => setEdit({ ...edit, state: e.target.value })} /></Field>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className={btnGreen}><CheckCircle2 size={12} /> Save</button>
            <button onClick={() => setEdit(null)} className={btnPlain}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================ PRODUCTS
const LIFECYCLES = ['development', 'phase_in', 'active', 'phase_out', 'discontinued'];

function Products({ reload }) {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('active');
  const [edit, setEdit] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [bulk, setBulk] = useState(null);

  const load = useCallback(async () => {
    const [p, v] = await Promise.all([
      axios.get(`${API}/inventory/products`, { params: { search, category, status } }),
      axios.get(`${API}/inventory/vendors`),
    ]);
    setData(p.data); setVendors(v.data.vendors || []);
  }, [search, category, status]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = { ...edit };
      ['sell_price', 'cost', 'box_qty', 'moq_units', 'reorder_point', 'safety_stock_days',
        'annual_forecast_qty', 'au_annual_forecast_qty', 'forecast_adjustment_percent',
        'weight'].forEach((k) => { body[k] = body[k] === '' || body[k] === undefined ? null : Number(body[k]); });
      if (!(body.item_number || '').trim()) throw new Error('Item number is required.');
      if (!(body.description || '').trim()) throw new Error('Description is required.');
      if (edit.id) await axios.put(`${API}/inventory/products/${edit.id}`, body);
      else await axios.post(`${API}/inventory/products`, body);
      setEdit(null); await load(); await reload();
    } catch (e) { window.alert(e.response?.data?.detail || e.message || 'Save failed.'); }
  };

  const exportCsv = async () => {
    const r = await axios.get(`${API}/inventory/products/export-csv`);
    const blob = new Blob([r.data.csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = r.data.filename; a.click();
    URL.revokeObjectURL(a.href);
  };

  const runImport = async (dry) => {
    try {
      const r = await axios.post(`${API}/inventory/products/import-csv`, { csv: bulk.text, dry_run: dry });
      setBulk({ ...bulk, result: r.data });
      if (!dry) { await load(); await reload(); }
    } catch (e) { window.alert(e.response?.data?.detail || 'Import failed.'); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <input className={`${inputCls} w-48`} placeholder="Search SKU / description / series" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className={`${inputCls} w-44`} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={`${inputCls} w-32`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">active</option><option value="archived">archived</option><option value="">all</option>
        </select>
        <div className="ml-auto flex gap-2">
          <button onClick={exportCsv} className={btnPlain}><Download size={11} /> Export CSV</button>
          <button onClick={() => setBulk({ text: '', result: null })} className={btnPlain}><Upload size={11} /> Import CSV</button>
          <button onClick={() => setEdit({ item_number: '', description: '', category: 'Miscellaneous', lifecycle: 'active', status: 'active', box_qty: 1, safety_stock_days: 14 })} className={btnGreen}>
            <Plus size={12} /> New product
          </button>
        </div>
      </div>

      {bulk && (
        <div className="border border-hg-border rounded-sm p-3 space-y-2 bg-hg-bg/40">
          <div className="text-[11px] font-sans text-hg-text-dim">
            Paste the edited CSV. Rows match on <span className="text-hg-text">item_number</span>;
            a <span className="text-hg-text">blank cell is left alone</span>, never blanked out.
            Dry run first — it shows every change before anything is written.
          </div>
          <textarea rows={6} className={inputCls} value={bulk.text} onChange={(e) => setBulk({ ...bulk, text: e.target.value })} placeholder="item_number,cost,vendor_name,annual_forecast_qty,…" />
          <div className="flex gap-2">
            <button onClick={() => runImport(true)} className={btnPlain}>Dry run</button>
            <button onClick={() => runImport(false)} disabled={!bulk.result} className={btnGreen}>Apply</button>
            <button onClick={() => setBulk(null)} className={btnPlain}>Close</button>
          </div>
          {bulk.result && (
            <div className="text-[11px] font-sans text-hg-text-dim space-y-1">
              <div>
                rows {bulk.result.rows} · matched {bulk.result.matched} · updated {bulk.result.updated}
                {' · '}unmatched {(bulk.result.unmatched || []).length} · invalid {(bulk.result.invalid || []).length}
                {bulk.result.dry_run && <span className="text-amber-300"> (dry run — nothing written)</span>}
              </div>
              {(bulk.result.unmatched || []).length > 0 && <div>no such SKU: {bulk.result.unmatched.slice(0, 12).join(', ')}</div>}
              {(bulk.result.invalid || []).slice(0, 8).map((iv, i) => (
                <div key={i} className="text-amber-300">{iv.item_number || `row ${iv.row}`}: {iv.column} {JSON.stringify(iv.value)} — {iv.reason}</div>
              ))}
              {(bulk.result.changes || []).slice(0, 10).map((c, i) => (
                <div key={i}>{c.item_number}: {Object.entries(c.diff).map(([k, d]) => `${k} ${JSON.stringify(d.from)}→${JSON.stringify(d.to)}`).join(', ')}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] font-sans text-hg-text-dim">{data.count} products</div>
      <div className="overflow-x-auto border border-hg-border rounded-sm">
        <table className="w-full text-[11px] font-sans">
          <thead className="bg-hg-bg/60 text-hg-text-dim">
            <tr>
              {['Item', 'Description', 'Category', 'Series', 'Lifecycle', 'Vendor', 'Cost', 'Box', 'MOQ', 'US fcst', 'AU fcst', ''].map((h) => (
                <th key={h} className="text-left px-2 py-1.5 font-normal uppercase tracking-wider text-[9px] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.products || []).map((p) => (
              <tr key={p.id} className="border-t border-hg-border/50 hover:bg-hg-bg/40">
                <td className="px-2 py-1.5 text-hg-text whitespace-nowrap">{p.item_number}</td>
                <td className="px-2 py-1.5 text-hg-text-dim max-w-[18rem] truncate">{p.description}</td>
                <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">{p.category}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(p.product_series)}</td>
                <td className="px-2 py-1.5">
                  <span className={`text-[9px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${['phase_out', 'discontinued'].includes(p.lifecycle) ? 'text-hg-text-dim border-hg-border' : p.lifecycle === 'active' ? 'text-hg-green border-hg-green/50' : 'text-blue-300 border-blue-400/40'}`}>
                    {N(p.lifecycle, 'active')}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(p.vendor_name)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{p.cost ? money(p.cost) : '—'}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(p.box_qty)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(p.moq_units)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(p.annual_forecast_qty)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(p.au_annual_forecast_qty)}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <button onClick={() => setEdit({ ...p })} className="text-hg-text-dim hover:text-hg-green"><Pencil size={11} /></button>
                  {p.status === 'active' ? (
                    <button title="Archive" onClick={async () => { await axios.post(`${API}/inventory/products/${p.id}/archive`); load(); }} className="ml-2 text-hg-text-dim hover:text-amber-300"><Archive size={11} /></button>
                  ) : (
                    <button title="Restore" onClick={async () => { await axios.post(`${API}/inventory/products/${p.id}/restore`); load(); }} className="ml-2 text-hg-text-dim hover:text-hg-green"><RotateCcw size={11} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="border border-hg-green/40 rounded-sm p-3 space-y-2 bg-hg-bg/40" data-testid="inv-product-form">
          <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">
            {edit.id ? `Edit ${edit.item_number}` : 'New product'}
          </div>
          <div className="grid md:grid-cols-4 gap-2">
            <Field label="Item number *"><input className={inputCls} value={edit.item_number || ''} onChange={(e) => setEdit({ ...edit, item_number: e.target.value })} /></Field>
            <Field label="Description *"><input className={inputCls} value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></Field>
            <Field label="Category *">
              <select className={inputCls} value={edit.category || ''} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>
                {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            {/* product_series and lifecycle were never on the Base44 form even though the AU
                forecast page depended on them — both are here. */}
            <Field label="Product series" hint="groups the order planner (CDA+, HS-1, T12 Satin)">
              <input className={inputCls} value={edit.product_series || ''} onChange={(e) => setEdit({ ...edit, product_series: e.target.value })} />
            </Field>
            <Field label="Lifecycle" hint="phase_out is never reordered">
              <select className={inputCls} value={edit.lifecycle || 'active'} onChange={(e) => setEdit({ ...edit, lifecycle: e.target.value })}>
                {LIFECYCLES.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Vendor">
              <select className={inputCls} value={edit.vendor_id || ''} onChange={(e) => setEdit({ ...edit, vendor_id: e.target.value || null })}>
                <option value="">— none —</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="Product line (MAS90)"><input className={inputCls} value={edit.product_line || ''} onChange={(e) => setEdit({ ...edit, product_line: e.target.value })} /></Field>
            <Field label="Cost"><input type="number" className={inputCls} value={edit.cost ?? ''} onChange={(e) => setEdit({ ...edit, cost: e.target.value })} /></Field>
            <Field label="Sell price"><input type="number" className={inputCls} value={edit.sell_price ?? ''} onChange={(e) => setEdit({ ...edit, sell_price: e.target.value })} /></Field>
            <Field label="Box qty" hint="pack-size rounding"><input type="number" className={inputCls} value={edit.box_qty ?? ''} onChange={(e) => setEdit({ ...edit, box_qty: e.target.value })} /></Field>
            <Field label="MOQ units" hint="vendor minimum for this SKU"><input type="number" className={inputCls} value={edit.moq_units ?? ''} onChange={(e) => setEdit({ ...edit, moq_units: e.target.value })} /></Field>
            <Field label="Safety stock days"><input type="number" className={inputCls} value={edit.safety_stock_days ?? ''} onChange={(e) => setEdit({ ...edit, safety_stock_days: e.target.value })} /></Field>
            <Field label="US annual forecast"><input type="number" className={inputCls} value={edit.annual_forecast_qty ?? ''} onChange={(e) => setEdit({ ...edit, annual_forecast_qty: e.target.value })} /></Field>
            <Field label="AU annual forecast" hint="Southport plans against this"><input type="number" className={inputCls} value={edit.au_annual_forecast_qty ?? ''} onChange={(e) => setEdit({ ...edit, au_annual_forecast_qty: e.target.value })} /></Field>
            <Field label="Forecast adjustment %"><input type="number" className={inputCls} value={edit.forecast_adjustment_percent ?? ''} onChange={(e) => setEdit({ ...edit, forecast_adjustment_percent: e.target.value })} /></Field>
            <Field label="Reorder point" hint="blank = (lead+safety)×daily usage"><input type="number" className={inputCls} value={edit.reorder_point ?? ''} onChange={(e) => setEdit({ ...edit, reorder_point: e.target.value })} /></Field>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className={btnGreen} data-testid="inv-product-save"><CheckCircle2 size={12} /> Save</button>
            <button onClick={() => setEdit(null)} className={btnPlain}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================ INVENTORY + COUNT MODE
function Levels({ reload }) {
  const [data, setData] = useState(null);
  const [facility, setFacility] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [countMode, setCountMode] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/inventory/levels`, { params: { facility_id: facility, status, search } });
    setData(r.data);
  }, [facility, status, search]);
  useEffect(() => { load(); }, [load]);

  const startCount = async (fid) => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/inventory/count-sheet`, { params: { facility_id: fid } });
      setCountMode({ ...r.data, entries: {}, result: null });
    } catch (e) { window.alert(e.response?.data?.detail || 'Could not open count mode.'); }
    setBusy(false);
  };

  const submitCount = async () => {
    const entries = Object.entries(countMode.entries)
      .filter(([, v]) => v !== '' && v !== null && v !== undefined)
      .map(([item_number, v]) => ({ item_number, counted_qty: Number(v) }));
    if (!entries.length) { window.alert('No counts entered.'); return; }
    if (!window.confirm(`Submit ${entries.length} counts for ${countMode.facility.name}? Every variance is written to the ledger.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/inventory/count/submit`, {
        facility_id: countMode.facility.id, entries,
      });
      setCountMode({ ...countMode, result: r.data });
      await load(); await reload();
    } catch (e) { window.alert(e.response?.data?.detail || 'Submit failed.'); }
    setBusy(false);
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  const c = data.counts || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        {['out', 'critical', 'order_now', 'order_soon', 'low', 'ok'].map((k) => (
          <button key={k} onClick={() => setStatus(status === k ? '' : k)}
            className={`bg-hg-surface border rounded-sm p-2 text-left ${status === k ? 'border-hg-green' : 'border-hg-border'}`}>
            <div className="text-[9px] font-sans uppercase tracking-wider text-hg-text-dim">{STATUS_LABEL[k]}</div>
            <div className="font-display text-lg text-hg-text">{num(c[k] || 0)}</div>
          </button>
        ))}
      </div>
      {(c.no_forecast || 0) > 0 && (
        <div className="text-[11px] font-sans text-hg-text-dim">
          <span className="text-amber-300">{num(c.no_forecast)}</span> product×facility pairs have
          no forecast, so they have no days-of-stock and are excluded from runout math — shown as
          “No forecast”, not as healthy.
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <select className={`${inputCls} w-64`} value={facility} onChange={(e) => setFacility(e.target.value)}>
          <option value="">All facilities</option>
          {(data.facilities || []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <input className={`${inputCls} w-48`} placeholder="Search SKU / description" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="ml-auto flex gap-2">
          {(data.facilities || []).map((f) => (
            <button key={f.id} onClick={() => startCount(f.id)} disabled={busy} className={btnGreen}>
              <ClipboardCheck size={12} /> Count {f.name.split(',')[0]}
            </button>
          ))}
        </div>
      </div>

      {countMode && (
        <div className="border border-hg-green/40 rounded-sm p-3 space-y-2 bg-hg-bg/40" data-testid="inv-count-mode">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-sans text-hg-text">
              Count mode — {countMode.facility.name}
              <span className="text-hg-text-dim"> · {countMode.count} products · walk the list, enter what you see</span>
            </div>
            <button onClick={() => setCountMode(null)} className={btnPlain}>Close</button>
          </div>
          <div className="text-[10px] font-sans text-hg-text-dim">
            Leave a row blank to skip it — a blank is “not counted”, never zero. Every variance
            becomes a ledger movement and stamps the count date.
          </div>
          {countMode.result ? (
            <div className="text-[11px] font-sans space-y-1">
              <div className="text-hg-green">
                Applied {countMode.result.applied} · {countMode.result.opening_counts} opening ·
                {' '}{countMode.result.adjustments} adjustments · {countMode.result.unchanged} unchanged
                {countMode.result.unmatched ? ` · ${countMode.result.unmatched} unmatched` : ''}
              </div>
              <div className="text-hg-text-dim">
                net units {countMode.result.net_units_change > 0 ? '+' : ''}{countMode.result.net_units_change}
              </div>
              {(countMode.result.variances || []).slice(0, 25).map((v, i) => (
                <div key={i} className="text-hg-text-dim">
                  {v.product_code}: {v.was} → {v.now} <span className={v.delta > 0 ? 'text-hg-green' : 'text-amber-300'}>({v.delta > 0 ? '+' : ''}{v.delta})</span>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="max-h-80 overflow-y-auto border border-hg-border rounded-sm">
                <table className="w-full text-[11px] font-sans">
                  <thead className="bg-hg-bg/60 text-hg-text-dim sticky top-0">
                    <tr>{['Item', 'Description', 'Category', 'Hub says', 'Counted'].map((h) => (
                      <th key={h} className="text-left px-2 py-1 font-normal uppercase tracking-wider text-[9px]">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {(countMode.rows || []).map((r) => (
                      <tr key={r.product_id} className="border-t border-hg-border/40">
                        <td className="px-2 py-1 text-hg-text whitespace-nowrap">{r.item_number}</td>
                        <td className="px-2 py-1 text-hg-text-dim max-w-[16rem] truncate">{r.description}</td>
                        <td className="px-2 py-1 text-hg-text-dim">{r.category}</td>
                        <td className="px-2 py-1 text-hg-text-dim">
                          {num(r.current_qty)}{r.never_counted && <span className="ml-1 text-amber-300 text-[9px]">never counted</span>}
                        </td>
                        <td className="px-2 py-1">
                          <input type="number" className="w-20 bg-hg-bg border border-hg-border rounded-sm px-1.5 py-0.5 text-[11px] text-hg-text"
                            value={countMode.entries[r.item_number] ?? ''}
                            onChange={(e) => setCountMode({ ...countMode, entries: { ...countMode.entries, [r.item_number]: e.target.value } })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={submitCount} disabled={busy} className={btnGreen} data-testid="inv-count-submit">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <ClipboardCheck size={12} />}
                {' '}Submit {Object.values(countMode.entries).filter((v) => v !== '').length} counts
              </button>
            </>
          )}
        </div>
      )}

      <div className="text-[11px] font-sans text-hg-text-dim">{data.count} rows</div>
      <div className="overflow-x-auto border border-hg-border rounded-sm">
        <table className="w-full text-[11px] font-sans">
          <thead className="bg-hg-bg/60 text-hg-text-dim">
            <tr>{['Item', 'Facility', 'On hand', 'Days left', 'Runs out', 'Order by', 'Status', 'Last counted'].map((h) => (
              <th key={h} className="text-left px-2 py-1.5 font-normal uppercase tracking-wider text-[9px] whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {(data.rows || []).slice(0, 400).map((r, i) => (
              <tr key={`${r.product_id}-${r.facility_id}-${i}`} className="border-t border-hg-border/50 hover:bg-hg-bg/40">
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <span className="text-hg-text">{r.product_code}</span>
                  <span className="text-hg-text-dim"> · {r.product_description}</span>
                </td>
                <td className="px-2 py-1.5 text-hg-text-dim whitespace-nowrap">
                  {(r.facility_name || '').split(',')[0]}
                  {r.region === 'au' && <span className="ml-1 text-purple-300 text-[9px]">AU</span>}
                </td>
                <td className="px-2 py-1.5 text-hg-text">{num(r.quantity_on_hand)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{r.days_of_stock === null ? '—' : num(r.days_of_stock)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(r.stockout_date)}</td>
                <td className="px-2 py-1.5 text-hg-text-dim">
                  {r.reorderable ? N(r.order_by_date) : <span className="text-hg-text-dim">no reorder</span>}
                </td>
                <td className="px-2 py-1.5"><Chip status={r.status} /></td>
                <td className="px-2 py-1.5 text-hg-text-dim">{N(r.last_counted_date, 'never')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(data.rows || []).length > 400 && (
        <div className="text-[10px] font-sans text-hg-text-dim">
          Showing the 400 most urgent of {data.count} rows — narrow with the filters above.
        </div>
      )}
    </div>
  );
}

// ============================================================ FORECASTING
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function Forecasting({ reload }) {
  const [region, setRegion] = useState('us');
  const [year] = useState(new Date().getFullYear());
  const [monthly, setMonthly] = useState(null);
  const [globalAdj, setGlobalAdj] = useState(0);
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [meta, setMeta] = useState(null);

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([
      axios.get(`${API}/inventory/forecast-settings`, { params: { year, region } }),
      axios.get(`${API}/inventory/forecast/products`, { params: { region, year, search } }),
    ]);
    const set = s.data.settings[region];
    setMonthly(set.monthly.map((x) => (x * 100).toFixed(2)));
    setGlobalAdj(set.global_adjustment_percent || 0);
    setRows(p.data.rows); setMeta(p.data);
  }, [region, year, search]);
  useEffect(() => { load(); }, [load]);

  const sum = (monthly || []).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const valid = Math.abs(sum - 100) <= 0.5;

  const saveCurve = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/inventory/forecast-settings`, {
        year, region, global_adjustment_percent: Number(globalAdj),
        monthly: monthly.map((x) => (parseFloat(x) || 0) / 100),
      });
      await load(); await reload();
    } catch (e) { window.alert(e.response?.data?.detail || 'Save failed.'); }
    setSaving(false);
  };

  const saveRow = async (r, field, value) => {
    try {
      await axios.put(`${API}/inventory/forecast/products/${r.product_id}`, {
        region, [field]: value === '' ? null : Number(value),
      });
      await load();
    } catch (e) { window.alert(e.response?.data?.detail || 'Save failed.'); }
  };

  if (!monthly || !rows) return <div className="flex justify-center py-10"><Loader2 size={18} className="animate-spin text-hg-green" /></div>;
  const maxPct = Math.max(...monthly.map((x) => parseFloat(x) || 0), 1);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {['us', 'au'].map((rg) => (
          <button key={rg} onClick={() => setRegion(rg)}
            className={`px-3 py-1.5 text-xs font-sans rounded-sm border ${region === rg ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-hg-border text-hg-text-dim'}`}>
            {rg === 'us' ? 'United States' : 'Australia'}
          </button>
        ))}
        <span className="text-[10px] font-sans text-hg-text-dim">
          {year} · region toggle replaces the separate AU tab. The AU curve is now actually
          applied to Southport, not just editable.
        </span>
      </div>

      <div className="border border-hg-border rounded-sm p-3 space-y-2">
        <div className="flex items-end gap-1.5">
          {monthly.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full bg-hg-green/70 rounded-sm" style={{ height: `${Math.max(4, (parseFloat(v) || 0) / maxPct * 70)}px` }} />
              <input className="w-full bg-hg-bg border border-hg-border rounded-sm px-1 py-0.5 text-[10px] text-center text-hg-text"
                value={v} onChange={(e) => { const m = [...monthly]; m[i] = e.target.value; setMonthly(m); }} />
              <div className="text-[9px] font-sans text-hg-text-dim">{MONTHS[i]}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className={`text-[11px] font-sans ${valid ? 'text-hg-green' : 'text-red-300'}`}>
            Σ {sum.toFixed(2)}% {valid ? '· valid' : '· must be 100% ±0.5% — save is blocked'}
          </div>
          <label className="text-[11px] font-sans text-hg-text-dim inline-flex items-center gap-1.5">
            Global adjustment %
            <input type="number" className="w-20 bg-hg-bg border border-hg-border rounded-sm px-1.5 py-0.5 text-[11px] text-hg-text"
              value={globalAdj} onChange={(e) => setGlobalAdj(e.target.value)} />
          </label>
          <button onClick={saveCurve} disabled={!valid || saving} className={btnGreen}>
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Save curve
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input className={`${inputCls} w-56`} placeholder="Search SKU / description" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="text-[11px] font-sans text-hg-text-dim">
          {meta?.count} products · <span className="text-amber-300">{meta?.missing_forecast}</span> with no {region.toUpperCase()} forecast
          {' '}(field <span className="text-hg-text">{meta?.forecast_field}</span>)
        </div>
      </div>

      <div className="overflow-x-auto border border-hg-border rounded-sm max-h-[30rem] overflow-y-auto">
        <table className="w-full text-[11px] font-sans">
          <thead className="bg-hg-bg/60 text-hg-text-dim sticky top-0">
            <tr>{['Item', 'Description', 'Series', `${region.toUpperCase()} annual qty`, 'Adj %', 'Adjusted total'].map((h) => (
              <th key={h} className="text-left px-2 py-1.5 font-normal uppercase tracking-wider text-[9px]">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.product_id} className="border-t border-hg-border/50">
                <td className="px-2 py-1 text-hg-text whitespace-nowrap">{r.item_number}</td>
                <td className="px-2 py-1 text-hg-text-dim max-w-[16rem] truncate">{r.description}</td>
                <td className="px-2 py-1 text-hg-text-dim">{N(r.product_series)}</td>
                <td className="px-2 py-1">
                  <input type="number" className="w-24 bg-hg-bg border border-hg-border rounded-sm px-1.5 py-0.5 text-[11px] text-hg-text"
                    defaultValue={r.annual_forecast_qty ?? ''}
                    onBlur={(e) => { if (String(e.target.value) !== String(r.annual_forecast_qty ?? '')) saveRow(r, 'annual_forecast_qty', e.target.value); }} />
                </td>
                <td className="px-2 py-1">
                  <input type="number" className="w-16 bg-hg-bg border border-hg-border rounded-sm px-1.5 py-0.5 text-[11px] text-hg-text"
                    defaultValue={r.forecast_adjustment_percent ?? ''}
                    onBlur={(e) => { if (String(e.target.value) !== String(r.forecast_adjustment_percent ?? '')) saveRow(r, 'forecast_adjustment_percent', e.target.value); }} />
                </td>
                <td className="px-2 py-1 text-hg-text-dim">
                  {r.has_forecast ? num(r.adjusted_annual_qty, 1) : <span className="text-hg-text-dim italic">no forecast</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================ SHELL
const TABS = [
  { id: 'overview', name: 'Overview', icon: Boxes },
  { id: 'levels', name: 'Inventory', icon: PackageSearch },
  { id: 'products', name: 'Products', icon: Boxes },
  { id: 'vendors', name: 'Vendors', icon: Truck },
  { id: 'forecast', name: 'Forecasting', icon: LineChart },
  { id: 'facilities', name: 'Facilities', icon: Building2 },
];

export default function Inventory() {
  const [tab, setTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/inventory/summary`),
        axios.get(`${API}/inventory/alerts`, { params: { status: 'active' } }),
      ]);
      setSummary(s.data); setAlerts(a.data.alerts || []); setErr('');
    } catch (e) { setErr(e.response?.data?.detail || 'Could not load inventory.'); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/inventory/run-check`);
      await reload();
      window.alert(
        `Check complete.\n\n`
        + `${r.data.pairs_evaluated} product×facility pairs evaluated\n`
        + `${r.data.alerts_created} alerts created `
        + `(${r.data.by_severity.critical} critical, ${r.data.by_severity.warning} warning, ${r.data.by_severity.info} info)\n`
        + `${r.data.skipped_no_forecast} skipped — no forecast\n`
        + `${r.data.auto_pos_created} draft POs created for approval\n\n`
        + `Nothing was sent.`);
    } catch (e) { window.alert(e.response?.data?.detail || 'Run failed.'); }
    setRunning(false);
  };

  return (
    <div className="space-y-4" data-testid="inventory">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-xl text-hg-text inline-flex items-center gap-2">
            <Boxes size={18} className="text-hg-green" /> Inventory
          </h1>
          <p className="text-[11px] font-sans text-hg-text-dim mt-0.5">
            Club components across Coeur d&apos;Alene and Southport. On-hand is the last physical
            count plus every ledger movement since — nothing sets a quantity without a reason
            attached. Nothing here emails anyone.
          </p>
        </div>
        <button onClick={reload} className={btnPlain}><RefreshCw size={12} /> Refresh</button>
      </div>

      {err && <div className="text-xs text-red-400 font-sans">{err}</div>}

      <div className="flex items-center gap-1 flex-wrap border-b border-hg-border pb-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`inv-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans rounded-sm border ${tab === t.id ? 'border-hg-green bg-hg-green/10 text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'}`}>
            <t.icon size={12} /> {t.name}
          </button>
        ))}
      </div>

      <Boundary name={tab}>
        {tab === 'overview' && <Overview summary={summary} alerts={alerts} onRun={runCheck} running={running} reload={reload} />}
        {tab === 'levels' && <Levels reload={reload} />}
        {tab === 'products' && <Products reload={reload} />}
        {tab === 'vendors' && <Vendors reload={reload} />}
        {tab === 'forecast' && <Forecasting reload={reload} />}
        {tab === 'facilities' && <Facilities reload={reload} />}
      </Boundary>
    </div>
  );
}
